import { ChangeEvent, useMemo } from "react";
import { Dayjs } from "dayjs";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import { Branch } from "../../types/user.type";
import DateRangePicker, { WITHIN_A_MONTH_PRESET_GROUPS } from "../common/DateRangePicker";
import CodeMultiSelect, { CodeOption } from "../DemandFactor/CodeMultiSelect";
import { REVIEW_MAX_DAYS } from "./constants";
import { REVIEW_STATUS_OPTIONS, ReviewStatusFilter } from "./dailyExpenseReview";

export interface ReviewFilterValue {
  dateFrom: Dayjs;
  dateTo: Dayjs;
  /** Branch codes; none = every branch. */
  branchCodes: string[];
  status: ReviewStatusFilter;
}

const RANGE_TOO_LONG_TEXT = `เลือกช่วงวันที่ได้ไม่เกิน ${REVIEW_MAX_DAYS} วัน`;

interface ReviewFiltersProps {
  value: ReviewFilterValue;
  branches: Branch[];
  /** The branch × day view reads every status, so the status filter rests there. */
  isStatusDisabled: boolean;
  isRangeTooLong: boolean;
  onChange: (patch: Partial<ReviewFilterValue>) => void;
}

const isReviewStatusFilter = (value: string): value is ReviewStatusFilter =>
  REVIEW_STATUS_OPTIONS.some((option) => option.value === value);

/** วันที่ (≤ 31 days), สาขา (several; none = all), สถานะ (default รออนุมัติ). */
export default function ReviewFilters({
  value,
  branches,
  isStatusDisabled,
  isRangeTooLong,
  onChange,
}: Readonly<ReviewFiltersProps>) {
  const branchOptions = useMemo<CodeOption[]>(
    () =>
      branches.map((branch) => ({
        code: branch.branch_code,
        label: `${branch.branch_code} - ${branch.branch_name}`,
      })),
    [branches],
  );
  const handleDateChange = (dateFrom: Dayjs, dateTo: Dayjs) => onChange({ dateFrom, dateTo });
  const handleBranchChange = (branchCodes: string[]) => onChange({ branchCodes });
  const handleStatusChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (isReviewStatusFilter(event.target.value)) onChange({ status: event.target.value });
  };

  return (
    <Paper>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 4 }}>
          <DateRangePicker
            dateFrom={value.dateFrom}
            dateTo={value.dateTo}
            onChange={handleDateChange}
            presetGroups={WITHIN_A_MONTH_PRESET_GROUPS}
          />
          {isRangeTooLong && <FormHelperText error>{RANGE_TOO_LONG_TEXT}</FormHelperText>}
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <CodeMultiSelect
            label="สาขา (ไม่เลือก = ทุกสาขา)"
            options={branchOptions}
            value={value.branchCodes}
            onChange={handleBranchChange}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            select
            size="small"
            fullWidth
            label="สถานะ"
            value={value.status}
            onChange={handleStatusChange}
            disabled={isStatusDisabled}
          >
            {REVIEW_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </Paper>
  );
}
